import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { otpDebugLog } from "./otpDebugLog";
import { isCompletedFormStatus, normalizeFormStatus } from "./formTabs";

export const formatDate = (dateString) => {
  if (!dateString) return "";

  try {
    if (/^\d+$/.test(dateString)) {
      const date = new Date(parseInt(dateString, 10) * 1000);
      if (isNaN(date.getTime())) {
        console.warn("Invalid Unix timestamp:", dateString);
        return "";
      }
      return date.toISOString().split("T")[0];
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn("Invalid date string:", dateString);
      return "";
    }
    return date.toISOString().split("T")[0];
  } catch (error) {
    console.error("Error formatting date:", error, "Input:", dateString);
    return "";
  }
};

export const getFormDetailsRecord = (response) =>
  response?.data?._id || response?.data?.title
    ? response.data
    : response?._id || response?.title
      ? response
      : response?.data;

export const formatQuestionResponses = (responses) => {
  if (!responses?.length) return [];

  const formattedResponses = responses.map((response) => {
    try {
      const parsedResponse = JSON.parse(response.response);
      // Check all possible response structures
      const formattedResponse = parsedResponse?.responses || {};

      return {
        ...response,
        formattedResponse,
        type: Object.values(formattedResponse)[0]?.type || "",
        value: Object.values(formattedResponse)[0]?.value || "",
      };
    } catch (error) {
      console.warn("Error parsing response:", error);
      return {
        ...response,
        formattedResponse: {},
        type: "",
        value: "",
      };
    }
  });

  // Log the entire formatted array

  return formattedResponses;
};
export const basePath = window.location.pathname.split("/")[1];

export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "active":
      return "bg-yellow text-yellow-800";
    case "draft":
      return "bg-orange text-white";
    case "not_active":
      return "bg-red-500 text-white";
    case "complete":
    case "completed":
    case "finished":
      return "bg-green text-white";
    case "ended":
      return "bg-gray-500 text-white";
    case "pending":
      return "bg-blue-500 text-white";
    default:
      return "bg-gray-300 text-gray-700";
  }
};

export const getStatusDisplayText = (status) => {
  const normalized = normalizeFormStatus(status);

  if (normalized === "not_active") {
    return "Inactive";
  }

  if (isCompletedFormStatus(normalized)) {
    return "Completed";
  }

  return String(status || "").replace(/_/g, " ");
};

export const languageOptions = [
  { value: "bsl-standard", label: "BSL - Standard" },
  { value: "bsl-scotland", label: "BSL - Dialect Scotland" },
  { value: "bsl-welsh", label: "BSL - Dialect Welsh" },
  { value: "bsl-northern", label: "BSL - Dialect Northern England" },
  { value: "bsl-southwestern", label: "BSL - Dialect South Western" },
  { value: "bsl-other", label: "BSL - Dialect Regional Other" },
  { value: "isl", label: "ISL (Irish Sign Language)" },
  { value: "is", label: "IS (International Sign)" },
  { value: "asl", label: "ASL (American Sign Language)" },
  { value: "lsf", label: "LSF (French Sign Language)" },
];

export const getQueryParams = (filters) => {
  const params = {};

  if (filters?.search?.trim()) {
    params.search = filters.search.trim();
  }

  if (filters?.organization) {
    params.organization = filters.organization;
  }

  if (filters?.interpreter) {
    params.interpreter = filters.interpreter;
  }

  if (filters?.language) {
    params.language = filters.language;
  }

  return params;
};

export const getScalePlaceholder = (points) => {
  switch (points.toString()) {
    case "4":
      return "Strongly Disagree, Disagree, Agree, Strongly Agree";
    case "7":
      return "Very Unsatisfied, Unsatisfied, Somewhat Unsatisfied, Neutral, Somewhat Satisfied, Satisfied, Very Satisfied";
    default: // 5 point scale
      return "Strongly Disagree, Disagree, Neutral, Agree, Strongly Agree";
  }
};

export const generateDeviceFingerprint = async () => {
  const startTime = Date.now();

  try {
    const fp = await Promise.race([
      FingerprintJS.load(),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("FingerprintJS load timeout")),
          10000,
        ),
      ),
    ]);

    // Get visitor identifier with timeout
    const result = await Promise.race([
      fp.get(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("FingerprintJS get timeout")), 10000),
      ),
    ]);

    // Extract stable components for our fingerprint
    const fingerprint = {
      visitorId: result.visitorId,
      userAgent: result.components.userAgent?.value || navigator.userAgent,
      platform: result.components.platform?.value || navigator.platform,
      screenResolution:
        result.components.screenResolution?.value ||
        `${screen.width}x${screen.height}`,
      timezone:
        result.components.timezone?.value ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      hardwareConcurrency:
        result.components.hardwareConcurrency?.value ||
        navigator.hardwareConcurrency ||
        "unknown",
      deviceMemory:
        result.components.deviceMemory?.value ||
        navigator.deviceMemory ||
        "unknown",
    };

    const duration = Date.now() - startTime;

    otpDebugLog("fingerprint.generate.success", {
      durationMs: duration,
      isFallback: false,
      hashPreview: result.visitorId
        ? `${String(result.visitorId).slice(0, 12)}…`
        : null,
    });

    return {
      fingerprint: fingerprint,
      hash: result.visitorId, // Use FingerprintJS visitorId as the hash
      timestamp: new Date().toISOString(),
      isFallback: false,
    };
  } catch (error) {
    const duration = Date.now() - startTime;
    // Log error with more details for debugging
    const errorDetails = {
      message: error?.message || "Unknown error",
      name: error?.name || "Error",
      stack: error?.stack,
      timestamp: new Date().toISOString(),
      duration: `${duration}ms`,
    };

    console.error(
      "[FINGERPRINT] Error generating device fingerprint:",
      errorDetails,
    );

    otpDebugLog("fingerprint.generate.error", errorDetails);

    // In production, you might want to send this error to your logging service
    // Example: if (import.meta.env.PROD && window.Sentry) {
    //   window.Sentry.captureException(error, {
    //     tags: { feature: 'trust-device' },
    //     extra: errorDetails
    //   });
    // }

    const fallbackSource = `${navigator.userAgent}|${navigator.platform}|${
      screen.width
    }x${screen.height}|${Intl.DateTimeFormat().resolvedOptions().timeZone}`;

    let fallbackHash = `fp_${fallbackSource.length}_${screen.width}x${screen.height}`;

    try {
      const bytes = new TextEncoder().encode(fallbackSource);
      const hashBuffer = await crypto.subtle.digest("SHA-256", bytes);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      fallbackHash = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
        .slice(0, 32);
    } catch {
      // Keep basic fallback hash when WebCrypto is unavailable.
    }

    const fallbackResult = {
      fingerprint: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        screenResolution: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timestamp: new Date().toISOString(),
        error: errorDetails.message, // Include error info for debugging
      },
      hash: fallbackHash,
      timestamp: new Date().toISOString(),
      isFallback: true, // Flag to indicate this is a fallback
    };

    otpDebugLog("fingerprint.generate.fallback", {
      hashPreview: `${String(fallbackHash).slice(0, 12)}…`,
      isFallback: true,
    });

    return fallbackResult;
  }
};

const TRUSTED_DEVICE_STORAGE_KEY = "unzippd_trusted_device_fingerprint";
const TRUSTED_DEVICE_COOKIE_KEY = "unzippd_trusted_device_fingerprint";

const getTrustedCookieDomain = () => {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;

  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return "";
  }

  if (host.endsWith(".unzippd.co.uk") || host === "unzippd.co.uk") {
    return ".unzippd.co.uk";
  }

  return "";
};

const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const cookieString = document.cookie || "";
  const target = `${name}=`;
  const found = cookieString
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(target));

  if (!found) return null;
  return found.slice(target.length);
};

const removeTrustedFingerprintCookie = () => {
  if (typeof document === "undefined") return;
  const domain = getTrustedCookieDomain();
  const attrs = ["Path=/", "Max-Age=0"];
  if (domain) attrs.push(`Domain=${domain}`);
  document.cookie = `${TRUSTED_DEVICE_COOKIE_KEY}=; ${attrs.join("; ")}`;
};

const writeTrustedFingerprintCookie = (payload, days = 30) => {
  if (typeof document === "undefined") return;
  const domain = getTrustedCookieDomain();
  const maxAge = Math.max(1, Math.floor(days * 24 * 60 * 60));
  const attrs = ["Path=/", `Max-Age=${maxAge}`, "SameSite=Lax"];

  if (window?.location?.protocol === "https:") {
    attrs.push("Secure");
  }
  if (domain) {
    attrs.push(`Domain=${domain}`);
  }

  document.cookie = `${TRUSTED_DEVICE_COOKIE_KEY}=${encodeURIComponent(
    JSON.stringify(payload),
  )}; ${attrs.join("; ")}`;
};

const normalizeTrustedDevicePayload = (parsed) => {
  if (!parsed?.hash) return null;

  if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
    return null;
  }

  return parsed;
};

export const getStoredTrustedDeviceFingerprint = () => {
  try {
    const cookieRaw = readCookie(TRUSTED_DEVICE_COOKIE_KEY);
    if (cookieRaw) {
      const parsedCookie = JSON.parse(decodeURIComponent(cookieRaw));
      const normalizedCookie = normalizeTrustedDevicePayload(parsedCookie);
      if (normalizedCookie) {
        otpDebugLog("trustedDevice.read", {
          source: "cookie",
          cookieDomain: getTrustedCookieDomain() || "(host-only)",
          hashPreview: `${String(normalizedCookie.hash).slice(0, 12)}…`,
          expiresAt: normalizedCookie.expiresAt,
        });
        return normalizedCookie;
      }

      removeTrustedFingerprintCookie();
      otpDebugLog("trustedDevice.read", {
        source: "cookie",
        status: "invalid_or_expired",
      });
    }

    const raw = localStorage.getItem(TRUSTED_DEVICE_STORAGE_KEY);

    if (!raw) {
      otpDebugLog("trustedDevice.read", {
        source: "localStorage",
        status: "empty",
      });
      return null;
    }

    const parsed = JSON.parse(raw);
    const normalized = normalizeTrustedDevicePayload(parsed);
    if (!normalized) {
      localStorage.removeItem(TRUSTED_DEVICE_STORAGE_KEY);
      otpDebugLog("trustedDevice.read", {
        source: "localStorage",
        status: "invalid_or_expired",
      });
      return null;
    }

    otpDebugLog("trustedDevice.read", {
      source: "localStorage",
      hashPreview: `${String(normalized.hash).slice(0, 12)}…`,
      expiresAt: normalized.expiresAt,
    });

    return normalized;
  } catch {
    otpDebugLog("trustedDevice.read", { status: "parse_error" });
    return null;
  }
};

export const storeTrustedDeviceFingerprint = (deviceData, days = 30) => {
  if (!deviceData?.hash) return;

  try {
    const expiresAt = Date.now() + days * 24 * 60 * 60 * 1000;
    const payload = {
      hash: deviceData.hash,
      fingerprint: deviceData.fingerprint || null,
      createdAt: Date.now(),
      expiresAt,
    };

    localStorage.setItem(TRUSTED_DEVICE_STORAGE_KEY, JSON.stringify(payload));
    writeTrustedFingerprintCookie(payload, days);
    otpDebugLog("trustedDevice.store", {
      ok: true,
      days,
      cookieDomain: getTrustedCookieDomain() || "(host-only)",
      hashPreview: `${String(deviceData.hash).slice(0, 12)}…`,
      expiresAt: payload.expiresAt,
    });
  } catch {
    // ignore localStorage failures (e.g. private mode restrictions)
    otpDebugLog("trustedDevice.store", {
      ok: false,
      reason: "localStorage_failed_try_cookie",
    });
    try {
      const expiresAt = Date.now() + days * 24 * 60 * 60 * 1000;
      writeTrustedFingerprintCookie(
        {
          hash: deviceData.hash,
          fingerprint: deviceData.fingerprint || null,
          createdAt: Date.now(),
          expiresAt,
        },
        days,
      );
      otpDebugLog("trustedDevice.store", {
        ok: true,
        days,
        via: "cookie_only",
        cookieDomain: getTrustedCookieDomain() || "(host-only)",
        hashPreview: `${String(deviceData.hash).slice(0, 12)}…`,
        expiresAt,
      });
    } catch {
      // ignore cookie failures
      otpDebugLog("trustedDevice.store", {
        ok: false,
        reason: "cookie_failed",
      });
    }
  }
};

export const getStableDeviceFingerprint = async () => {
  const trusted = getStoredTrustedDeviceFingerprint();

  if (trusted?.hash) {
    otpDebugLog("fingerprint.stable", {
      fromTrustedStorage: true,
      hashPreview: `${String(trusted.hash).slice(0, 12)}…`,
    });
    return {
      hash: trusted.hash,
      fingerprint: trusted.fingerprint || null,
      fromTrustedStorage: true,
    };
  }

  const generated = await generateDeviceFingerprint();

  otpDebugLog("fingerprint.stable", {
    fromTrustedStorage: false,
    hashPreview: generated?.hash
      ? `${String(generated.hash).slice(0, 12)}…`
      : "unknown",
    isFallback: Boolean(generated?.isFallback),
  });

  return {
    hash: generated?.hash || "unknown",
    fingerprint: generated?.fingerprint || null,
    fromTrustedStorage: false,
  };
};
